import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useProductConfigsByStudioIdMutation } from "entities/products/queries/mutateProductConfigsByStudioId";
import {
  invalidateProductsByStudioIdQuery,
  useProductsByStudioIdQuery,
} from "entities/products/queries/useProductsByStudioIdQuery";
import { ProductsConfigs } from "entities/products/types";
import { invalidateStudioDetailsQuery } from "entities/studios/queries/useStudioDetailsQuery";
import { useUpdateStudioQuery } from "entities/studios/queries/useUpdateStudioQuery";
import { StudioDetailsTabs, StudioUpdate } from "entities/studios/types";
import { useStudiosPage } from "modules/studios/context";
import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { useImageFilesQueries } from "shared/queries/useImageFileQueries";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { ManagedTranslations, StudioFile, StudioType } from "./types";

const [Provider, useStudioDetails] = createContext<StudioType>({
  name: "StudioDetailsContext",
  hookName: "useStudioDetails",
  providerName: "StudioDetailsProvider",
});

const StudioDetailsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { allowed: hasReadAccess } = useRolePermission("studios:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "studios:translations:read",
  );
  const { studioDetailsDrawer, productDetailsDrawer } = useStudiosPage();
  const [logoFile, setLogoFile] = useState<StudioFile>(null);
  const [selectedTab, setSelectedTab] = useState(StudioDetailsTabs.General);
  const studioName: InputHookResult = useInputState("studioName", "");
  const studioDescription: InputHookResult = useInputState(
    "studioDescription",
    "",
  );
  const [studioCurrencies, setStudioCurrencies] = useState<string[]>([]);
  const [translations, setTranslations] = useState<Translations | undefined>();
  const [productConfigs, setProductConfigs] = useState<
    ProductsConfigs | undefined
  >();

  const { mutateAsync: mutateUpdateStudio, isPending: isSubmittingStudio } =
    useUpdateStudioQuery();

  const { mutateAsync: mutateProductConfigs } =
    useProductConfigsByStudioIdMutation();

  const { data: productList = [] } = useProductsByStudioIdQuery(
    studioDetailsDrawer.drawerDetails?.id,
  );

  useEffect(() => {
    const productDetailsId = productDetailsDrawer.drawerDetails?.id;

    if (!productDetailsId) return;

    const productConfigExists = productConfigs?.[productDetailsId];

    if (!productConfigExists) {
      const defaultConfigs = productList.find(
        (product) => product.id === productDetailsId,
      )?.configurations;

      if (defaultConfigs) {
        setProductConfigs((prev) => ({
          ...prev,
          [productDetailsId]: defaultConfigs,
        }));
      }
    }
  }, [productList, productDetailsDrawer.drawerDetails?.id, productConfigs]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(StudioDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, studioName]);

  const studioDetails = useMemo(
    () => studioDetailsDrawer.drawerDetails,
    [studioDetailsDrawer.drawerDetails],
  );

  useEffect(() => {
    if (studioDetails) {
      studioName.setValue(studioDetails?.name ?? "");
      studioDescription.setValue(studioDetails?.description ?? "");
      setStudioCurrencies(studioDetails?.currencies ?? []);
      setTranslations(studioDetails?.translations);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioDetails]);

  const queries = useImageFilesQueries({
    queries: [
      {
        queryKey: ["studio-logo-file", (studioDetails?.id ?? "").toString()],
        path: studioDetails?.logo,
        setImageFile: setLogoFile,
      },
    ],
    enabled: Boolean(studioDetails?.id),
  });

  const isLoadingLogoFile = queries.every((query) => query.isFetching);

  const translationsWithDefaultValues = {
    ...translations,
    en: {
      ...translations?.en,
      [ManagedTranslations.Name]: studioName.input.value,
    },
  };

  const onCloseStudioDetails = () => {
    studioDetailsDrawer.closeDrawer();
    setSelectedTab(StudioDetailsTabs.General);
    setTranslations(undefined);
    setProductConfigs(undefined);
  };

  const handleUpdateProductsConfigs = async () => {
    if (productConfigs && studioDetails?.id) {
      await mutateProductConfigs(
        {
          studioId: studioDetails.id.toString(),
          productConfigs,
        },
        {
          onSuccess: () => invalidateProductsByStudioIdQuery(studioDetails.id),
        },
      );
    }
  };

  const handleUpdateStudio = async () => {
    const studio = {
      ...studioDetails,
      name: studioName.input.value,
      description: studioDescription.input.value,
      currencies: studioCurrencies,
      logo: logoFile,
      translations: translationsWithDefaultValues,
    } as StudioUpdate;

    await mutateUpdateStudio(
      { studio },
      {
        onSuccess: () => {
          onCloseStudioDetails();
          invalidateStudioDetailsQuery(
            studioDetailsDrawer.drawerDetails?.id ?? "",
          );
        },
      },
    );
  };

  const onSaveStudioDetails = async () => {
    if (!studioName.input.value) {
      return studioName.setErrors([
        { key: "studioName", message: "Field is required" },
      ]);
    }

    try {
      await Promise.all([handleUpdateStudio(), handleUpdateProductsConfigs()]);
    } catch (e) {
      console.error(e);
    }
  };

  const onSaveStudioTranslations = async () => {
    const studio = {
      translations: translationsWithDefaultValues,
    } as StudioUpdate;

    await mutateUpdateStudio(
      { studio },
      {
        onSuccess: () => {
          onCloseStudioDetails();
          invalidateStudioDetailsQuery(
            studioDetailsDrawer.drawerDetails?.id ?? "",
          );
        },
      },
    );
  };

  const value = {
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    studioDetails,
    logoFile,
    setLogoFile,
    onSaveStudioDetails,
    onSaveStudioTranslations,
    isSubmittingStudio,
    isLoadingLogoFile,
    studioName,
    studioDescription,
    studioCurrencies,
    setStudioCurrencies,
    productConfigs,
    setProductConfigs,
    onCloseStudioDetails,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { StudioDetailsProvider, useStudioDetails };
